import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { Clock, ExternalLink, Image, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { AdminDataGrid } from '@/components/grid/AdminDataGrid'
import { PaginationControls } from '@/components/grid/PaginationControls'
import { AppShell } from '@/components/layout/AppShell'
import { useAuth } from '@/hooks/use-auth'
import { cn, formatDateTime, timeAgo } from '@/lib/utils'
import { useMonitorStore } from '@/stores/monitor-store'

const TYPE_TABS = [
  { value: '', label: 'All' },
  { value: 'compose', label: 'Try-On' },
  { value: 'enhance', label: 'Enhancement' },
]

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'processing', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
]

const statusStyles: Record<string, string> = {
  completed: 'bg-positive/10 text-positive border-positive/20',
  processing: 'bg-warning/10 text-warning border-warning/20',
  failed: 'bg-negative/10 text-negative border-negative/20',
}

function formatMs(ms: number | null): string {
  if (ms === null) return '—'
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function getRequestType(tag: string | null): { label: string; style: string } {
  if (tag === 'person')
    return { label: 'Try-On', style: 'bg-accent/10 text-accent border-accent/20' }
  if (tag === 'clothing')
    return { label: 'Enhancement', style: 'bg-positive/10 text-positive border-positive/20' }
  return { label: 'Unknown', style: 'bg-ink-faint/10 text-ink-faint border-ink-faint/20' }
}

type RequestRow = ReturnType<typeof useMonitorStore.getState>['requests'][number]
type RequestCellParams = ICellRendererParams<RequestRow>

export function RequestLog() {
  useAuth()
  const {
    requests,
    requestsPagination,
    isLoadingRequests,
    requestDetail,
    isLoadingDetail,
    typeFilter,
    statusFilter,
    dateFrom,
    dateTo,
    setTypeFilter,
    setStatusFilter,
    setDateFrom,
    setDateTo,
    setUserSearch,
    fetchRequests,
    fetchRequestDetail,
    clearDetail,
  } = useMonitorStore()

  const [searchInput, setSearchInput] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  const handleSearch = () => {
    setUserSearch(searchInput)
    fetchRequests(1)
  }

  const handleDateFilter = () => {
    fetchRequests(1)
  }

  const handleRowClick = (id: string) => {
    if (selectedId === id) {
      setSelectedId(null)
      clearDetail()
    } else {
      setSelectedId(id)
      fetchRequestDetail(id)
    }
  }

  const requestColumns: Array<ColDef<RequestRow>> = [
    {
      field: 'createdAt',
      headerName: 'Time',
      minWidth: 140,
      maxWidth: 160,
      cellRenderer: (params: RequestCellParams) =>
        params.data ? (
          <span
            className="font-mono text-[11px] text-ink-faint"
            title={params.data.createdAt ? formatDateTime(params.data.createdAt) : ''}
          >
            {params.data.createdAt ? timeAgo(params.data.createdAt) : '—'}
          </span>
        ) : null,
    },
    {
      headerName: 'User',
      flex: 1.2,
      minWidth: 220,
      cellRenderer: (params: RequestCellParams) =>
        params.data ? (
          <div className="min-w-0">
            <p className="truncate font-mono text-xs text-ink">{params.data.userName ?? '—'}</p>
            <p className="truncate font-mono text-[10px] text-ink-faint">
              {params.data.userEmail ?? params.data.userId}
            </p>
          </div>
        ) : null,
    },
    {
      headerName: 'Type',
      minWidth: 130,
      maxWidth: 150,
      cellRenderer: (params: RequestCellParams) => {
        const data = params.data
        if (!data) return null
        const type = getRequestType(data.personImageTag)
        return (
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-medium',
              type.style
            )}
          >
            {type.label}
          </span>
        )
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      maxWidth: 160,
      cellRenderer: (params: RequestCellParams) =>
        params.data ? (
          <span
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-[10px] font-medium',
              statusStyles[params.data.status] ?? statusStyles.processing
            )}
          >
            {params.data.status}
          </span>
        ) : null,
    },
    {
      field: 'generationTimeMs',
      headerName: 'Duration',
      minWidth: 120,
      maxWidth: 140,
      cellRenderer: (params: RequestCellParams) =>
        params.data ? (
          <div className="flex items-center gap-1 font-mono text-[11px] tabular-nums text-ink-faint">
            <Clock className="h-3 w-3" />
            {formatMs(params.data.generationTimeMs)}
          </div>
        ) : null,
    },
    {
      field: 'creditsUsed',
      headerName: 'Credits',
      minWidth: 100,
      maxWidth: 120,
      cellRenderer: (params: RequestCellParams) =>
        params.data ? (
          <span className="font-mono text-xs tabular-nums text-ink">
            {params.data.creditsUsed ?? 0}
          </span>
        ) : null,
    },
    {
      field: 'personImageUri',
      headerName: 'Image',
      minWidth: 100,
      maxWidth: 120,
      cellRenderer: (params: RequestCellParams) => {
        const data = params.data
        if (!data) return null

        return data.personImageUri ? (
          <a
            href={data.personImageUri}
            target="_blank"
            rel="noopener noreferrer"
            className="group/img relative inline-block"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={data.personImageUri}
              alt=""
              className="h-8 w-8 rounded border border-border object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 flex items-center justify-center rounded bg-ink/40 opacity-0 transition-opacity group-hover/img:opacity-100">
              <ExternalLink className="h-3 w-3 text-white" />
            </div>
          </a>
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded border border-dashed border-border">
            <Image className="h-3.5 w-3.5 text-ink-faint" />
          </div>
        )
      },
    },
  ]

  return (
    <AppShell title="Request Log">
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-md border border-dashed border-border bg-surface">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setTypeFilter(tab.value)}
                className={cn(
                  'px-3 py-2 font-mono text-[11px] transition-colors first:rounded-l-md last:rounded-r-md',
                  typeFilter === tab.value
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-surface-alt'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex rounded-md border border-dashed border-border bg-surface">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={cn(
                  'px-3 py-2 font-mono text-[11px] transition-colors first:rounded-l-md last:rounded-r-md',
                  statusFilter === tab.value
                    ? 'bg-accent/10 text-accent'
                    : 'text-ink-muted hover:bg-surface-alt'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Filter by user ID..."
                className="w-52 rounded-md border border-dashed border-border bg-surface py-2 pl-8 pr-3 font-mono text-[11px] text-ink placeholder:text-ink-faint"
              />
            </div>
            <button
              type="button"
              onClick={handleSearch}
              className="rounded-md border border-dashed border-border bg-surface px-3 py-2 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
            >
              Filter
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-md border border-dashed border-border bg-surface px-2.5 py-2 font-mono text-[11px] text-ink"
            />
            <span className="font-mono text-[10px] text-ink-faint">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-md border border-dashed border-border bg-surface px-2.5 py-2 font-mono text-[11px] text-ink"
            />
            <button
              type="button"
              onClick={handleDateFilter}
              className="rounded-md border border-dashed border-border bg-surface px-3 py-2 font-mono text-[11px] text-ink-muted transition-colors hover:bg-surface-alt"
            >
              Apply
            </button>
          </div>
        </div>

        <AdminDataGrid
          columns={requestColumns}
          emptyMessage="No requests found"
          getRowClass={({ data }) =>
            data?.id === selectedId ? 'lookbook-grid-row-selected cursor-pointer' : 'cursor-pointer'
          }
          getRowId={({ data }) => data.id}
          loading={isLoadingRequests}
          onRowClicked={({ data }) => {
            if (data) handleRowClick(data.id)
          }}
          rows={requests}
        />

        {selectedId ? (
          <div className="rounded-lg border border-dashed border-accent/30 bg-accent/5 px-6 py-5">
            {isLoadingDetail ? (
              <p className="font-mono text-xs text-ink-faint animate-pulse">Loading details...</p>
            ) : requestDetail ? (
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <p className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
                    Request Detail
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedId(null)
                      clearDetail()
                    }}
                    className="rounded p-1 text-ink-faint transition-colors hover:bg-surface-alt hover:text-ink"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  <DetailField label="Request ID" value={requestDetail.id} mono />
                  <DetailField label="User ID" value={requestDetail.userId} mono />
                  <DetailField label="IP Address" value={requestDetail.requestIp ?? '—'} mono />
                  <DetailField label="Style" value={requestDetail.style ?? '—'} />
                </div>

                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  <DetailField
                    label="Generation Time"
                    value={formatMs(requestDetail.generationTimeMs)}
                  />
                  <DetailField
                    label="Total Time"
                    value={formatMs(requestDetail.totalRequestTimeMs)}
                  />
                  <DetailField
                    label="Started"
                    value={requestDetail.startedAt ? formatDateTime(requestDetail.startedAt) : '—'}
                  />
                  <DetailField
                    label="Completed"
                    value={
                      requestDetail.completedAt ? formatDateTime(requestDetail.completedAt) : '—'
                    }
                  />
                </div>

                {requestDetail.customPrompt ? (
                  <div>
                    <p className="mb-1 font-mono text-[10px] text-ink-faint uppercase">
                      Custom Prompt
                    </p>
                    <p className="rounded border border-dashed border-border bg-surface p-3 font-mono text-[11px] leading-relaxed text-ink">
                      {requestDetail.customPrompt}
                    </p>
                  </div>
                ) : null}

                {requestDetail.generatedPrompt ? (
                  <div>
                    <p className="mb-1 font-mono text-[10px] text-ink-faint uppercase">
                      Generated Prompt
                    </p>
                    <p className="rounded border border-dashed border-border bg-surface p-3 font-mono text-[11px] leading-relaxed text-ink">
                      {requestDetail.generatedPrompt}
                    </p>
                  </div>
                ) : null}

                {requestDetail.errorMessage ? (
                  <div className="rounded border border-dashed border-negative/30 bg-negative/5 p-3">
                    <p className="mb-1 font-mono text-[10px] text-negative uppercase">
                      Error {requestDetail.errorCode ? `(${requestDetail.errorCode})` : ''}
                    </p>
                    <p className="font-mono text-[11px] text-negative/80">
                      {requestDetail.errorMessage}
                    </p>
                  </div>
                ) : null}

                {requestDetail.userAgent ? (
                  <DetailField label="User Agent" value={requestDetail.userAgent} />
                ) : null}

                <div>
                  <p className="mb-2 font-mono text-[10px] text-ink-faint uppercase">Images</p>
                  <div className="flex flex-wrap gap-3">
                    {requestDetail.personImageUri ? (
                      <a
                        href={requestDetail.personImageUri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/img relative"
                      >
                        <img
                          src={requestDetail.personImageUri}
                          alt="Person"
                          className="h-24 w-24 rounded border border-border object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 flex items-center justify-center rounded bg-ink/40 opacity-0 transition-opacity group-hover/img:opacity-100">
                          <ExternalLink className="h-4 w-4 text-white" />
                        </div>
                        <span className="mt-1 block font-mono text-[9px] text-ink-faint">
                          Person
                        </span>
                      </a>
                    ) : null}
                    {Array.isArray(requestDetail.clothingImages)
                      ? (requestDetail.clothingImages as Array<{ uri?: string }>).map((img, idx) =>
                          img.uri ? (
                            <a
                              key={`clothing-${img.uri}`}
                              href={img.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="group/img relative"
                            >
                              <img
                                src={img.uri}
                                alt={`Clothing ${idx + 1}`}
                                className="h-24 w-24 rounded border border-border object-cover"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 flex items-center justify-center rounded bg-ink/40 opacity-0 transition-opacity group-hover/img:opacity-100">
                                <ExternalLink className="h-4 w-4 text-white" />
                              </div>
                              <span className="mt-1 block font-mono text-[9px] text-ink-faint">
                                Clothing {idx + 1}
                              </span>
                            </a>
                          ) : null
                        )
                      : null}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <PaginationControls
          label="requests"
          onNext={() => fetchRequests(requestsPagination.page + 1)}
          onPrevious={() => fetchRequests(requestsPagination.page - 1)}
          page={requestsPagination.page}
          total={requestsPagination.total}
          totalPages={requestsPagination.totalPages}
        />
      </div>
    </AppShell>
  )
}

function DetailField({
  label,
  value,
  mono = false,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div>
      <p className="mb-0.5 font-mono text-[10px] text-ink-faint uppercase">{label}</p>
      <p className={cn('text-[11px] text-ink break-all', mono ? 'font-mono' : 'font-mono')}>
        {value}
      </p>
    </div>
  )
}
